/**
 * The real transport for the editor surface. ONE `fetch`, the same construction as
 * ./http-cabinet-api.ts and for the same reasons — bearer only, no cookies, cross-origin
 * (adr/0002 puts the cabinet on `authors.moonx.app` and the API on `moonx.art/authors-api`).
 *
 * ★★ WHY THIS IS NOT `createHttpCabinetApi` WITH TWENTY MORE ENTRIES. ./http-cabinet-api.ts is
 * shared by every other cabinet screen and its `request` throws a bare `ApiProblem` for every
 * refusal. Two of this surface's refusals carry an EXTENSION MEMBER that `ApiProblem` does not
 * keep — the 409's whole working copy and the 422's whole failure set — so the decode here has to
 * look at the payload before throwing. Adding that branch to the shared transport would put an
 * editor-shaped special case in the path of the payments screen.
 *
 * NOTHING IN THIS FILE RUNS UNLESS A BUILD SETS `VITE_AUTHORS_API_LIVE=true` (`dev:stack` does). It
 * was written beside the fixture, because a transport written later against screens that grew up
 * on fixtures is a transport that discovers six shape mismatches on its first day — the same
 * argument ./http-cabinet-api.ts makes.
 */
import { AUTHORS_API_BASE_URL } from '../../../config';
import { i18n } from '../../../i18n';
import { sendWithSession } from '../../auth/session/send-with-session';
import {
  AutosaveConflict,
  StructuralLimitsRefusal,
  readConflictCurrent,
  readStructuralFailures,
} from './editor-api';
import { ApiProblem, NotFoundFailure, TransportFailure, retryAfterOf } from './problem';
import type { ArticleImageClaim, AutosaveRequest, EditorApi } from './editor-api';
import type { OperationQuery, Problem, PublicId } from './generated';
import type { AccessTokenSource } from './http-cabinet-api';

export interface HttpEditorApiOptions {
  readonly baseUrl?: string;
  readonly getAccessToken: AccessTokenSource;
}

type QueryValue = string | number | boolean | undefined | null;

function toSearch(query: Readonly<Record<string, QueryValue>> | undefined): string {
  if (query === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue;
    }
    params.append(key, String(value));
  }
  const search = params.toString();
  return search.length > 0 ? `?${search}` : '';
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<Problem>;
  return typeof candidate.error_code === 'string' && typeof candidate.status === 'number';
}

/** The article id goes in a path segment on fourteen of these twenty routes. */
function seg(id: string): string {
  return encodeURIComponent(id);
}

export function createHttpEditorApi(options: HttpEditorApiOptions): EditorApi {
  const baseUrl = options.baseUrl ?? AUTHORS_API_BASE_URL;

  async function request(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    init: {
      query?: Readonly<Record<string, QueryValue>> | undefined;
      body?: unknown;
      signal?: AbortSignal | undefined;
    } = {},
  ): Promise<unknown> {
    // The 401 rule - one forced refresh and one retry on a bare 401, and a refused credential ends
    // the session - is the shared one (features/auth/session/send-with-session.ts). Autosave's PUT
    // is replayed with the rest: a 401 was refused by the guard before the handler read anything.
    const response = await sendWithSession(options.getAccessToken, async (token) => {
      const headers = new Headers({ Accept: 'application/json' });
      if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (init.body !== undefined) {
        headers.set('Content-Type', 'application/json');
      }
      try {
        return await fetch(`${baseUrl}${path}${toSearch(init.query)}`, {
          method,
          headers,
          body: init.body === undefined ? null : JSON.stringify(init.body),
          ...(init.signal === undefined ? {} : { signal: init.signal }),
        });
      } catch (cause) {
        // An AbortError is the caller's own doing and must stay one, so use-async can ignore it
        // rather than painting an error state over a screen the author has left.
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          throw cause;
        }
        throw new TransportFailure(
          'The request did not reach the platform.',
          cause === undefined ? undefined : { cause },
        );
      }
    });

    // `removeArticleVideo` answers 204 with no body to decode.
    if (response.status === 204) {
      return undefined;
    }

    if (!response.ok) {
      const retryAfter = retryAfterOf(response.headers);
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = undefined;
      }

      if (isProblem(payload)) {
        /* ★ THE TWO EXTENSION MEMBERS, READ BEFORE THE THROW. Both are lost the moment a bare
           `ApiProblem` is constructed (problem.ts:50-56 copies five members), and the 409's is the
           only copy of the server's document the client will be given — the contract removed the
           re-fetch deliberately. */
        if (response.status === 409) {
          const current = readConflictCurrent(payload);
          if (current !== undefined) {
            throw new AutosaveConflict(payload, current);
          }
        }
        if (response.status === 422) {
          const failures = readStructuralFailures(payload);
          if (failures !== undefined) {
            throw new StructuralLimitsRefusal(payload, failures);
          }
        }
        throw new ApiProblem(payload, retryAfter);
      }

      if (response.status === 404) {
        /* A 404 WITH NO PROBLEM BODY AT ALL. `NotFoundFailure` exists because `ErrorCode` used to
           have no not-found member; problem.ts:70-86 says so in the past tense since `e7fe057` —
           the enum now carries a plain `NOT_FOUND`, so a deployment CAN send a problem
           document here and the branch above will take it. This is only the fallback for a bare
           404 - a proxy's, say - which carries no document and so no code for anything to
           name. */
        throw new NotFoundFailure('That article is no longer there.', 'article');
      }
      throw new TransportFailure(`The platform answered ${String(response.status)}.`);
    }

    return await response.json();
  }

  return {
    getOwnArticle: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${seg(articleId)}`, { signal }) as Promise<
        Awaited<ReturnType<EditorApi['getOwnArticle']>>
      >,

    autosaveArticle: (articleId: PublicId, body: AutosaveRequest) =>
      request('PUT', `/v1/articles/${seg(articleId)}`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['autosaveArticle']>>
      >,

    getStructuralRules: (signal, query: OperationQuery<'getStructuralRules'> | undefined) =>
      request('GET', '/v1/rules/structural', {
        query: query as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['getStructuralRules']>>>,

    // The names come in the cabinet's language of the moment (design 29, W5); the screen keys its
    // read on that language, so a change asks again.
    listEditorCategories: (signal) =>
      request('GET', '/v1/categories', { query: { lang: i18n.language }, signal }) as Promise<
        Awaited<ReturnType<EditorApi['listEditorCategories']>>
      >,

    /* Asked with nothing typed, the answer also carries MoonX's own list as `groups` (design 24,
       T6); asked with `q`, only `items`. Both pass through as the server sends them. */
    suggestTags: (query: OperationQuery<'suggestTags'> | undefined, signal) =>
      request('GET', '/v1/tags/suggestions', {
        query: query as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['suggestTags']>>>,

    getPublishDateSuggestions: (
      query: OperationQuery<'getPublishDateSuggestions'> | undefined,
      signal,
    ) =>
      request('GET', '/v1/calendar/suggestions', {
        query: query as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['getPublishDateSuggestions']>>>,

    checkArticleSlugAvailability: (
      articleId: PublicId,
      query: OperationQuery<'checkArticleSlugAvailability'>,
      signal,
    ) =>
      request('GET', `/v1/articles/${seg(articleId)}/slug/availability`, {
        query: query as Readonly<Record<string, QueryValue>>,
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['checkArticleSlugAvailability']>>>,

    claimArticleImageUpload: (articleId: PublicId, body: ArticleImageClaim) =>
      request('POST', `/v1/articles/${seg(articleId)}/images`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['claimArticleImageUpload']>>
      >,

    mintDraftPreviewLink: (articleId: PublicId) =>
      request('POST', `/v1/articles/${seg(articleId)}/preview-link`) as Promise<
        Awaited<ReturnType<EditorApi['mintDraftPreviewLink']>>
      >,

    getArticleReview: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${seg(articleId)}/review`, { signal }) as Promise<
        Awaited<ReturnType<EditorApi['getArticleReview']>>
      >,

    /* The ordinal is 1-based and the review sequence is per-article; both are path segments, which
       is why a finding is addressed by (article, reviewSeq, ordinal) and never by a row id. */
    resolveReviewFinding: (articleId: PublicId, reviewSeq: number, ordinal: number) =>
      request(
        'POST',
        `/v1/articles/${seg(articleId)}/reviews/${seg(String(reviewSeq))}/findings/${seg(
          String(ordinal),
        )}/resolve`,
      ) as Promise<Awaited<ReturnType<EditorApi['resolveReviewFinding']>>>,

    /* An article sends NO REQUEST BODY on either transition: the button seals what the author is
       looking at. A video sends its confirmed rules (v1.0.29). Both answer 202. */
    submitArticle: (articleId: PublicId, body) =>
      request('POST', `/v1/articles/${seg(articleId)}/submit`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['submitArticle']>>
      >,

    resubmitArticle: (articleId: PublicId, body) =>
      request('POST', `/v1/articles/${seg(articleId)}/resubmit`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['resubmitArticle']>>
      >,

    // `lang` names the groups in the answer (design 30), in the cabinet's language of the moment.
    listArticleCalendarEvents: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${seg(articleId)}/calendar-events`, {
        query: { lang: i18n.language },
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['listArticleCalendarEvents']>>>,

    /* A WHOLE-SET PUT, and the body is built by the caller from ids and relations only: the write
       schema is strict, so a link echoed with its read-only `title` would be a 400. */
    setArticleCalendarEvents: (articleId: PublicId, body) =>
      request('PUT', `/v1/articles/${seg(articleId)}/calendar-events`, {
        query: { lang: i18n.language },
        body,
      }) as Promise<Awaited<ReturnType<EditorApi['setArticleCalendarEvents']>>>,

    /* THE THREE VERSION OPERATIONS (./editor-api.ts, `ArticleVersionsApi`). The list sends no
       query at all: it is not paged, and an unknown query member is the pipe's 400. */
    listArticleVersions: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${seg(articleId)}/versions`, { signal }) as Promise<
        Awaited<ReturnType<EditorApi['listArticleVersions']>>
      >,

    getArticleVersion: (
      articleId: PublicId,
      revisionNo: number,
      query: OperationQuery<'getArticleVersion'> | undefined,
      signal,
    ) =>
      request('GET', `/v1/articles/${seg(articleId)}/versions/${seg(String(revisionNo))}`, {
        query: query as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      }) as Promise<Awaited<ReturnType<EditorApi['getArticleVersion']>>>,

    /* NO REQUEST BODY: the operation seals the working copy the autosave has written. 201. */
    saveArticleVersion: (articleId: PublicId) =>
      request('POST', `/v1/articles/${seg(articleId)}/versions`) as Promise<
        Awaited<ReturnType<EditorApi['saveArticleVersion']>>
      >,

    getOwnProfile: (signal) =>
      request('GET', '/v1/profile', { signal }) as Promise<
        Awaited<ReturnType<EditorApi['getOwnProfile']>>
      >,

    /* NO QUERY: the operation declares no parameter, and the set is the same for every caller. */
    listSourceLanguages: (signal) =>
      request('GET', '/v1/rules/source-languages', { signal }) as Promise<
        Awaited<ReturnType<EditorApi['listSourceLanguages']>>
      >,

    // A video's video (v1.0.29, ./editor-api.ts `ArticleVideoApi`).
    getArticleVideo: (articleId: PublicId, signal) =>
      request('GET', `/v1/articles/${seg(articleId)}/video`, { signal }) as Promise<
        Awaited<ReturnType<EditorApi['getArticleVideo']>>
      >,

    setArticleVideo: (articleId: PublicId, body) =>
      request('PUT', `/v1/articles/${seg(articleId)}/video`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['setArticleVideo']>>
      >,

    removeArticleVideo: async (articleId: PublicId) => {
      await request('DELETE', `/v1/articles/${seg(articleId)}/video`);
    },

    setVideoCover: (articleId: PublicId, body) =>
      request('POST', `/v1/articles/${seg(articleId)}/video/cover`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['setVideoCover']>>
      >,

    setVideoDraftConfirmations: (articleId: PublicId, body) =>
      request('PUT', `/v1/articles/${seg(articleId)}/video/confirmations`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['setVideoDraftConfirmations']>>
      >,

    setStreamTimes: (articleId: PublicId, body) =>
      request('PUT', `/v1/articles/${seg(articleId)}/stream`, { body }) as Promise<
        Awaited<ReturnType<EditorApi['setStreamTimes']>>
      >,
  };
}
